import { AutoComplete, Alert, Button, Card, Col, Form, Input, InputNumber, Row, Select, Typography } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { jsonRule } from '../JsonField';
import { CategorySelect } from '../CategorySelect';

export const STATUS_OPTIONS = [
  { value: 'active', label: 'Hoạt động' },
  { value: 'disabled', label: 'Đã tắt' },
  { value: 'maintenance', label: 'Bảo trì' },
];

export const AUTH_OPTIONS = [
  { value: 'none', label: 'Không' },
  { value: 'bearer', label: 'Bearer Token' },
  { value: 'api_key_header', label: 'API Key (Header)' },
  { value: 'api_key_query', label: 'API Key (Query)' },
  { value: 'basic', label: 'Basic Auth' },
];

export const ADAPTER_OPTIONS = [
  { value: 'generic-http', label: 'generic-http' },
  { value: 'dola-webhook', label: 'dola-webhook (có webhook)' },
  { value: 'plenxai-free-webhook', label: 'plenxai-free-webhook (có webhook)' },
  { value: 'gpt-playwright-webhook', label: 'gpt-playwright-webhook (có webhook)' },
  { value: 'dreamface-webhook', label: 'dreamface-webhook (lipsync · có webhook)' },
  { value: 'zapcap-webhook', label: 'zapcap-webhook (caption video · có webhook)' },
  { value: 'videogen-webhook', label: 'videogen-webhook (tạo video t2v/i2v · có webhook)' },
  { value: 'watermark-webhook', label: 'watermark-webhook (xoá watermark video/ảnh · có webhook)' },
  { value: 'vilao', label: 'vilao (OpenAI-compatible · nguồn con)' },
  { value: 'gpt-smart', label: 'gpt-smart (codex-imagen · nguồn con)' },
  { value: 'zora', label: 'zora (OpenAI-compatible · tạo/sửa ảnh)' },
  { value: 'apiai', label: 'apiai (OpenAI-compatible · chat/tạo/sửa ảnh)' },
  { value: 'tramsangtao', label: 'tramsangtao (ảnh/video/TTS/chat · polling, không webhook)' },
  { value: 'kapend', label: 'kapend (ảnh/video/chat · đồng bộ + polling, không webhook)' },
  { value: 'mock', label: 'mock' },
];

/** Adapter dùng cơ chế webhook (hiện bảng thông tin webhook + ô Secret HMAC). */
const WEBHOOK_ADAPTERS = ['dola-webhook', 'plenxai-free-webhook', 'gpt-playwright-webhook', 'dreamface-webhook', 'zapcap-webhook', 'videogen-webhook', 'watermark-webhook'];

const cardTitleStyle = { color: '#2563eb' };

const EXEC_MODE_OPTIONS = [
  { value: 'hybrid', label: 'Tự động (Hybrid)' },
  { value: 'worker', label: 'Worker' },
  { value: 'gateway', label: 'Gateway' },
];

/** Các Form.Item của nhà cung cấp — chia 2 thẻ: thông tin cơ bản & kết nối.
 *  `pollingCategories`: gợi ý danh mục (lấy từ model của nhà cung cấp) cho cấu hình polling. */
export default function ProviderFormFields({ pollingCategories = [] }: { pollingCategories?: string[] }) {
  const catOptions = pollingCategories.map((c) => ({ value: c }));
  return (
    <Row gutter={[16, 16]} align="stretch">
      {/* ----- Thông tin cơ bản ----- */}
      <Col xs={24} lg={12}>
        <Card title={<span style={cardTitleStyle}>Thông tin cơ bản</span>} style={{ height: '100%' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Tên"
                rules={[{ required: true, message: 'Vui lòng nhập tên' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="slug"
                label="Slug"
                rules={[{ required: true, message: 'Vui lòng nhập slug' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Mô tả">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="category" label="Danh mục">
                <CategorySelect />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="version" label="Phiên bản">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="Trạng thái">
                <Select options={STATUS_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="logo" label="Logo (URL)">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="baseUrl" label="Base URL">
                <Input placeholder="https://api.example.com" />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Col>

      {/* ----- Thông tin kết nối ----- */}
      <Col xs={24} lg={12}>
        <Card title={<span style={cardTitleStyle}>Thông tin kết nối</span>} style={{ height: '100%' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="adapter" label="Adapter">
                <Select options={ADAPTER_OPTIONS} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="executionMode" label="Chế độ thực thi">
                <Select options={EXEC_MODE_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="authType" label="Loại xác thực">
                <Select options={AUTH_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>
          {/* Bảng thông tin webhook + ô Secret — hiện với các adapter dùng webhook */}
          <Form.Item noStyle shouldUpdate={(p, c) => p.adapter !== c.adapter}>
            {({ getFieldValue }) => {
              const adapter = getFieldValue('adapter');
              if (!WEBHOOK_ADAPTERS.includes(adapter)) return null;
              const isPlenxaiFree = adapter === 'plenxai-free-webhook';
              const isGptPlaywright = adapter === 'gpt-playwright-webhook';
              const isDreamface = adapter === 'dreamface-webhook';
              const isZapcap = adapter === 'zapcap-webhook';
              const isVideogen = adapter === 'videogen-webhook';
              const isWatermark = adapter === 'watermark-webhook';
              // Webhook TOÀN CỤC (admin dán URL + Secret một lần bên nguồn): Videogen, Watermark.
              const isGlobalWebhook = isVideogen || isWatermark;
              const callbackPath = isGptPlaywright
                ? '/api/v1/providers/gpt-playwright/callback'
                : isPlenxaiFree
                ? '/api/v1/providers/plenxai-free/callback'
                : isDreamface
                ? '/api/v1/providers/dreamface/callback'
                : isZapcap
                ? '/api/v1/providers/zapcap/callback'
                : isVideogen
                ? '/api/v1/providers/videogen/callback'
                : isWatermark
                ? '/api/v1/providers/watermark/callback'
                : '/api/v1/providers/dola/callback';
              const title = isGptPlaywright
                ? 'Webhook GPT Playwright — URL nhận kết quả (tự động)'
                : isPlenxaiFree
                ? 'Webhook Plenxai Free — URL nhận kết quả (tự động)'
                : isDreamface
                ? 'Webhook Dreamface (lipsync) — URL nhận kết quả (tự động)'
                : isZapcap
                ? 'Webhook Zapcap (caption video) — URL nhận kết quả (tự động)'
                : isVideogen
                ? 'Webhook Videogen (tạo video) — URL nhận kết quả (tự động)'
                : isWatermark
                ? 'Webhook Watermark (xoá watermark) — dán URL này vào nguồn'
                : 'Webhook Dola — URL nhận kết quả (tự động)';
              return (
                <>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={title}
                    description={
                      <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                        {isGptPlaywright ? (
                          <div>
                            Gateway <b>tự đăng ký webhook</b> với GPT Playwright ở lần chạy đầu (dùng Secret bên dưới).
                            Nếu nguồn không có API đăng ký, hãy <b>dán URL bên dưới</b> vào dashboard ChatGPT (tab Webhooks) thủ công.
                          </div>
                        ) : isPlenxaiFree ? (
                          <div>
                            Gateway <b>tự đăng ký webhook</b> với Plenxai (POST <code>/api/webhooks</code>) ở lần chạy đầu,
                            dùng đúng Secret bên dưới. Bạn <b>không cần</b> đăng ký thủ công.
                          </div>
                        ) : isGlobalWebhook ? (
                          <div>
                            Nguồn này dùng <b>webhook toàn cục</b>: hãy <b>dán URL bên dưới</b> + đúng Secret vào
                            trang <b>Webhook &amp; API</b> của nguồn (một lần). Gateway khớp job theo{' '}
                            <code>job_id</code> và verify HMAC bằng Secret này.
                          </div>
                        ) : (
                          <div>
                            Gateway <b>tự gửi</b> <code>callback_url</code> riêng cho mỗi job (kèm nonce bí mật).
                            Bạn <b>không cần</b> nhập URL ở đâu cả.
                          </div>
                        )}
                        <div style={{ marginTop: 6 }}>Endpoint nhận webhook (cố định):</div>
                        <Typography.Text copyable code>
                          {`${window.location.origin}${callbackPath}`}
                        </Typography.Text>
                      </div>
                    }
                  />
                  <Form.Item
                    name="webhookSecret"
                    label="Secret ký HMAC (webhook)"
                    tooltip={
                      isGptPlaywright
                        ? 'Shared secret để xác thực callback. Gateway dùng secret này khi đăng ký webhook + verify HMAC sha256(rawBody) trên X-Webhook-Signature. Copy đúng secret từ dashboard ChatGPT. Để trống = không ký.'
                        : isPlenxaiFree
                        ? 'Shared secret để xác thực callback. Gateway dùng secret này khi đăng ký webhook với Plenxai, rồi verify HMAC (ts.rawBody). Để trống = không tự đăng ký/không ký.'
                        : isWatermark
                        ? 'Shared secret của webhook bên Watermark (trang "Webhook & API"). Gateway verify HMAC sha256(secret, "<timestamp>." + rawBody) trên X-Watermark-Signature. Để trống = không kiểm chữ ký.'
                        : 'Shared secret để xác thực + toàn vẹn callback. Gateway gửi kèm callback_secret cho nguồn ký per-job, rồi tự verify HMAC sha256(rawBody). Để trống = không ký (chỉ dựa nonce).'
                    }
                  >
                    <Input.Password placeholder="Để trống nếu không ký HMAC" autoComplete="new-password" />
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>
          <Form.Item name="apiKey" label="API Key">
            <Input.Password />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="timeoutMs" label="Timeout (ms)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="retryCount"
                label="Số lần tạo lại khi lỗi"
                tooltip="0 = không tạo lại. Nếu lớn hơn 0, request lỗi nguồn sẽ được giữ lại và tạo lại đủ số lần này trước khi báo lỗi cuối."
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="rateLimitPerMin"
                label="Giới hạn request/phút"
                tooltip="Số request tối đa gửi tới nhà cung cấp này trong 1 phút. Vượt giới hạn -> request sau được đưa vào danh sách chờ và xử lý ở phút kế tiếp. Để trống hoặc 0 = không giới hạn."
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder="vd 10 (trống/0 = không giới hạn)" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="maxConcurrent"
                label="Số request đồng thời tối đa"
                tooltip="Số request được xử lý CÙNG LÚC tối đa với nhà cung cấp này (vd GPT Playwright). Slot được giữ tới khi job kết thúc. Vượt giới hạn -> request sau được đưa vào danh sách chờ tới khi có slot trống. Để trống hoặc 0 = không giới hạn."
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder="vd 3 (trống/0 = không giới hạn)" />
              </Form.Item>
            </Col>
          </Row>
          {/* Endpoint submit/status/cancel — KHÔNG áp dụng cho nguồn con Vilao/GPT Smart (chat sync, ảnh qua codex-imagen). */}
          <Form.Item noStyle shouldUpdate={(p, c) => p.adapter !== c.adapter}>
            {({ getFieldValue }) =>
              getFieldValue("adapter") === "vilao" || getFieldValue("adapter") === "gpt-smart" ? null : (
                <>
                  <Form.Item name="submitEndpoint" label="Submit Endpoint">
                    <Input />
                  </Form.Item>
                  <Form.Item name="statusEndpoint" label="Status Endpoint">
                    <Input />
                  </Form.Item>
                  <Form.Item name="cancelEndpoint" label="Cancel Endpoint">
                    <Input />
                  </Form.Item>
                </>
              )
            }
          </Form.Item>
          <Form.Item name="headers" label="Headers (JSON)" rules={[jsonRule]}>
            <Input.TextArea rows={3} style={{ fontFamily: 'monospace' }} placeholder="{ }" />
          </Form.Item>
          <Form.Item name="config" label="Config (JSON)" rules={[jsonRule]}>
            <Input.TextArea rows={3} style={{ fontFamily: 'monospace' }} placeholder="{ }" />
          </Form.Item>
        </Card>
      </Col>

      {/* ----- Cấu hình Polling theo danh mục (chỉ nguồn polling, KHÔNG áp dụng webhook) ----- */}
      <Col xs={24}>
        <Form.Item noStyle shouldUpdate={(p, c) => p.adapter !== c.adapter}>
          {({ getFieldValue }) => {
            // Nguồn webhook + Vilao/GPT Smart (chat sync / ảnh qua codex-imagen) không cần polling -> ẩn thẻ này.
            const ad = getFieldValue('adapter');
            if (WEBHOOK_ADAPTERS.includes(ad) || ad === 'vilao' || ad === 'gpt-smart') return null;
            return (
              <Card title={<span style={cardTitleStyle}>Cấu hình Polling theo danh mục</span>}>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message='Chờ ban đầu (không poll) để tiết kiệm tài nguyên, sau đó poll theo chu kỳ đến khi đạt "Tối đa chờ".'
                  description="Mỗi dòng áp dụng cho 1 danh mục model (vd Tao anh: chờ 40s / chu kỳ 5s / tối đa 4 phút). Có thể ghi đè riêng từng model ở tab Model → Sửa."
                />
                <Form.List name="pollingRows">
                  {(rows, { add, remove }) => (
                    <>
                      {rows.map((row) => (
                        <Row gutter={12} key={row.key} align="middle" style={{ marginBottom: 4 }}>
                          <Col xs={24} md={7}>
                            <Form.Item
                              name={[row.name, 'category']}
                              label="Danh mục áp dụng"
                              rules={[{ required: true, message: 'Chọn danh mục' }]}
                            >
                              <AutoComplete
                                options={catOptions}
                                placeholder="vd Tao anh"
                                filterOption={(input, opt) =>
                                  String(opt?.value ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={7} md={5}>
                            <Form.Item name={[row.name, 'pollInitialDelaySec']} label="Chờ ban đầu (giây)">
                              <InputNumber min={0} style={{ width: '100%' }} placeholder="40" />
                            </Form.Item>
                          </Col>
                          <Col xs={7} md={5}>
                            <Form.Item name={[row.name, 'pollIntervalSec']} label="Chu kỳ poll (giây)">
                              <InputNumber min={1} style={{ width: '100%' }} placeholder="5" />
                            </Form.Item>
                          </Col>
                          <Col xs={7} md={5}>
                            <Form.Item name={[row.name, 'pollMaxWaitMin']} label="Tối đa chờ (phút)">
                              <InputNumber min={1} style={{ width: '100%' }} placeholder="4" />
                            </Form.Item>
                          </Col>
                          <Col xs={3} md={2}>
                            <Button
                              type="text"
                              danger
                              icon={<MinusCircleOutlined />}
                              onClick={() => remove(row.name)}
                            />
                          </Col>
                        </Row>
                      ))}
                      <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block>
                        Thêm danh mục
                      </Button>
                    </>
                  )}
                </Form.List>
              </Card>
            );
          }}
        </Form.Item>
      </Col>
    </Row>
  );
}
